import { useQuery } from 'react-query'

import { useCatalogService } from 'features/catalog/service'
import type { SourceGroup } from 'features/catalog/types'
import type { AppliedFilterOptionRange } from 'features/filters/types'
import { useProductService } from 'features/products/service'
import type { ProductHierarchyInfo } from 'features/products/types'
import type { Filters } from 'types/project'

export function useCatalogListQueries(
  projectId: string,
  selectedLens: string | null,
  localFilters?: Filters,
): {
  hierarchyData: ProductHierarchyInfo[] | undefined
  hierarchyLoading: boolean
  sourceData: SourceGroup[] | undefined
  sourcesLoading: boolean
  syndicatedSourceData: SourceGroup[] | undefined
  syndicatedSourceLoading: boolean
} {
  const catalogService = useCatalogService()
  const productService = useProductService()

  const createTimeFilter = localFilters?.values?.find(
    (f) => f.field === 'create_time',
  ) as AppliedFilterOptionRange | undefined
  const dateMin = createTimeFilter?.min
  const dateMax = createTimeFilter?.max

  const { isLoading: hierarchyLoading, data: hierarchyData } = useQuery(
    [
      'getProductHierarchiesForProject',
      projectId,
      dateMin,
      dateMax,
      'reviews_only',
    ],
    () =>
      productService.getProductHierarchiesForProject(
        'getProductHierarchiesForProject',
        projectId,
        dateMin,
        dateMax,
        'reviews_only',
      ),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: !!projectId && selectedLens === 'product_hierarchy',
    },
  )

  const { data: sourceData, isLoading: sourcesLoading } = useQuery(
    ['sourcegroup', false, projectId, 'by_country', dateMin, dateMax],
    () =>
      catalogService.getSourceGroups(
        'sourcegroup',
        false,
        projectId,
        true,
        dateMin,
        dateMax,
      ),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: !!projectId && selectedLens === 'source_type',
    },
  )

  const { data: syndicatedSourceData, isLoading: syndicatedSourceLoading } =
    useQuery(
      ['syndicated_source_group', true, projectId, dateMin, dateMax],
      () =>
        catalogService.getSourceGroups(
          'syndicated_source_group',
          true,
          projectId,
          false,
          dateMin,
          dateMax,
        ),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: Infinity,
        cacheTime: Infinity,
        enabled: !!projectId && selectedLens === 'source_type',
      },
    )

  return {
    hierarchyData,
    hierarchyLoading,
    sourceData,
    sourcesLoading,
    syndicatedSourceData,
    syndicatedSourceLoading,
  }
}
